import { ActionType, ProColumns } from "@ant-design/pro-components";
import { DatePicker, Form, Input, Popconfirm, Tooltip, message } from 'antd';
import { ROUTES } from 'base/routes/routes';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomButton from "base/ui/components/Button";
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { CheckStatus, CloseStatus, Plus } from 'base/ui/components/Icons';
import dayjs from 'dayjs';
import useDebounce from 'hooks/useDebounce';
import usePartialState from 'hooks/usePartialState';
import { cloneDeep, isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import { getDropdownCatalog } from 'pages/Common/dropdown';
import { ButtonEdit } from 'pages/Components/FunctionButton';
import PageWrapper from 'pages/Components/PageWrapper';
import { SearchItem } from 'pages/Components/SearchBox';
import { dropdownstatus } from 'pages/Components/dataDropDown';
import { SearchModel } from 'pages/model';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsTrash } from 'react-icons/bs';
import { Link, useNavigate } from 'react-router-dom';
import { CommonModel } from 'services/Main/common_services';
import { commonPresenter, commonStore, documentArchivePresenter, documentArchiveStore } from 'stores/root_store';
import { PAGE_SIZE_DEFAULT } from 'utils/constants';
import { getPlantCode } from 'utils/localStorageHelper';
import { checkPermission, useCheckPermission } from 'utils/permissionHelper';

const { RangePicker } = DatePicker;

export type ArchivesModel = {
  stt: number,
  id: string,
  documentName: string,
  documentType: string,
  infoType: string,
  productRef: string,
  description: string,
  createTime: string,
  actived: boolean,
}


// KHO TÀI LIỆU Page
const createPageArchives = () => {
  return observer(() => {

    useCheckPermission("INDEX");

    // ============ Navigation =================/
    const navigate = useNavigate();

    const [form] = Form.useForm();
    const tableRef = useRef<ActionType>();

    const { t } = useTranslation();

    // ==================================================================================================
    const [loading, setLoading] = usePartialState({
      button: false
    });

    // data common date
    const [listCommonDate, setListCommonDate] = useState<CommonModel[]>([]);
    // disable rangePicker khi chọn ngày từ common
    const [disableRangePicker, setDisableRangePicker] = useState(false);
    // Tìm kiếm / phân trang
    const [dataPaging, setDataPaging] = useState<SearchModel>({
      paging: {
        pageSize: PAGE_SIZE_DEFAULT,
        pageIndex: 1,
        orderBy: undefined,
        orderByDesc: undefined,
      },
      name: undefined,
      actived: true,
      companyCode: getPlantCode()
    });

    const columns: ProColumns<ArchivesModel>[] = [
      // STT
      {
        title: "STT",
        dataIndex: "stt",
        key: "stt",
        align: "center",
        width: 50,
      },
      // Tên tài liệu
      {
        title: "Tên tài liệu",
        dataIndex: "documentName",
        key: "documentName",
        align: "center",
        render: (text, record) => (
          <Link
            to={
              ROUTES.UTILITIES.ARCHIVES.LINK +
              `?type=detail&tabId=${record.id}&tabName=${record.documentName}`
            }
          >
            {text}
          </Link>
        ),
      },
      // Loại tài liệu
      {
        title: "Loại tài liệu",
        dataIndex: "documentType",
        key: "documentType",
      },
      // Loại thông tin
      {
        title: "Loại thông tin",
        dataIndex: "infoType",
        key: "infoType",
      },
      // Sản phẩm liên quan
      {
        title: "Sản phẩm liên quan",
        dataIndex: "productRef",
        key: "productRef",
        width: 150,
        ellipsis: true
      },
      // Công ty
      {
        title: "Công ty",
        dataIndex: "companyName",
        key: "companyName",
        width: 200,
        ellipsis: true
      },
      // Phòng ban
      {
        title: "Phòng ban",
        dataIndex: "departmentName",
        key: "departmentName",
        width: 200,
        ellipsis: true
      },
      // Người tạo
      {
        title: "Người tạo",
        dataIndex: "createByName",
        key: "createByName",
      },
      // Mô tả
      {
        title: "Mô tả",
        dataIndex: "description",
        key: "description",
        width: 300,
        ellipsis: true
      },
      // thời gian tạo
      {
        title: "Thời gian tạo",
        dataIndex: "createTime",
        key: "createTime",
        align: "center",
        valueType: "dateTime",
        fieldProps: {
          format: "DD/MM/YYYY HH:mm:ss",
        },
        width: 150,
        ellipsis: true
      },
      // thời gian ngưng sử dụng
      {
        title: "Thời gian ngưng sử dụng",
        dataIndex: "unActivedTime",
        key: "unActivedTime",
        align: "center",
        valueType: "dateTime",
        fieldProps: {
          format: "DD/MM/YYYY HH:mm:ss",
        },
      },
      // trạng thái
      {
        title: "Trạng thái",
        dataIndex: "actived",
        key: "actived",
        render: (_, record) =>
          record.actived ? (
            <CheckStatus width="14" className="!fill-[#0052cc] block mx-auto" />
          ) : (
            <CloseStatus width="14" className="!fill-red-500 block mx-auto" />
          ),
      },
      // Chức năng
      {
        title: "Chức năng",
        dataIndex: "action",
        key: "action",
        onCell: () => {
          return {
            style: {
              textAlign: "center",
            },
          };
        },
        render: (text, record) => {
          return (
            <div className='flex gap-2 items-center'>
              {checkPermission('EDIT') && (
                <ButtonEdit onClick={() => {
                  navigate(ROUTES.UTILITIES.ARCHIVES.LINK +
                    `?type=detail&tabId=${record.id}&tabName=${record.documentName}`)
                }} />
              )}
              {checkPermission('DELETE') && (
                <Popconfirm
                  title="Ngưng sử dụng"
                  description="Bạn có xác nhận ngưng sử dụng tài liệu này"
                  onConfirm={async () => {
                    await documentArchivePresenter.deleteArchive(documentArchiveStore, record.id)
                    if (documentArchiveStore.createUpdate.isSuccess) {
                      message.success(documentArchiveStore.createUpdate.message)
                      tableRef.current?.reload()
                    }
                  }}
                >
                  {/* <ButtonIcon
                  color="#389e0d"
                  className={`!leading-[25px] !h-[25px]`}
                >
                </ButtonIcon> */}
                  <Tooltip title={"Ngưng sử dụng"}>
                    <BsTrash className="text-xl text-red-500 cursor-pointer" />
                  </Tooltip>
                </Popconfirm>
              )}

            </div >
          );
        },
      },
    ];


    const [dataTable, setDataTable] = useState<ArchivesModel[]>([])

    // loại tài liệu
    const [documentType, setdocumentType] = useState<CommonModel[]>([])
    // loại thông tin
    const [infoType, setinfoType] = useState<CommonModel[]>([])
    // sản phẩm liên quan
    const [listProduct, setListProduct] = useState<CommonModel[]>([])
    // tên tài liệu
    const [documentName, setdocumentName] = useState<CommonModel[]>([])
    // Phòng ban
    const [listDepartment, setListDepartment] = useState<CommonModel[]>([]);
    // Công ty
    const [listCompany, setListCompany] = useState<CommonModel[]>([]);

    const [listCreateBy, setListCreateBy] = useState<CommonModel[]>([]);



    // ====================================================================================================
    // Lấy danh sách common date
    const getListCommonDate = async () => {
      await commonPresenter.getListSelectCommonDate(commonStore);
      if (commonStore.commonRespone.isSuccess) {
        setListCommonDate(commonStore.commonRespone.data);
      }
    };

    // Lấy ngày bằng common data
    const getDateByKey = async (key: string) => {
      await commonPresenter.getDateByCommonDate(commonStore, key);
      if (commonStore.commonDate.fromDate) {
        form.setFieldValue("createTime", [
          dayjs(commonStore.commonDate.fromDate),
          dayjs(commonStore.commonDate.toDate),
        ]);
      } else {
        form.setFieldValue("createTime", undefined);
      }
    };

    const getListData = async (dataSearch: any) => {
      await documentArchivePresenter.getListDocumentArchive(documentArchiveStore, dataSearch);
      if (documentArchiveStore.documentArchive.isSuccess) {
        const dataTemp = cloneDeep(documentArchiveStore.documentArchive.data);
        setDataTable(dataTemp);
        return dataTemp;
      }
      setDataTable([]);
      return [];
    };

    // Công ty
    const getDataCompany = async () => {
      await commonPresenter.getListSaleOrg(commonStore);
      if (commonStore.commonRespone.isSuccess) {
        setListCompany(commonStore.commonRespone.data);
      }
    };
    // -------------------------------------------------------------------------------------------------  ----------------------------------------------------------------
    // Phòng ban
    const getDataDepartment = async (key?: string) => {
      const congty = form.getFieldValue('companyCode')
      await commonPresenter.getListDepartment(commonStore, key, congty);
      if (commonStore.commonRespone.isSuccess) {
        setListDepartment(commonStore.commonRespone.data);
      }
    };

    // Lấy  danh sách người tạo
    const getListCreateBy = async (key = '') => {
      await commonPresenter.getListCreateBy(commonStore, key);
      if (commonStore.commonRespone.isSuccess) {
        setListCreateBy(commonStore.commonRespone.data);
      }
    };
    const searchCreateBy = useDebounce(getListCreateBy, 500);

    // search dropdown keyword tên tài liệu
    const getDropdownDocumentName = async (key?: string) => {
      const actived = form.getFieldValue('actived')
      const companyCode = form.getFieldValue('companyCode')
      await documentArchivePresenter.getDropdownDocumentName(documentArchiveStore, key, actived, companyCode);
      if (documentArchiveStore.dropdownDocName.isSuccess) {
        setdocumentName(documentArchiveStore.dropdownDocName.data)
      }
    }
    const debounceDocumentName = useDebounce(getDropdownDocumentName, 300)

    useEffect(() => {
      // sản phẩm liên quan
      getDropdownCatalog({ onSetDropdown: setListProduct, params: { catalogTypeCode: 'AssociatedProducts' } })
      // loại thông tin
      getDropdownCatalog({ onSetDropdown: setinfoType, params: { catalogTypeCode: 'InformationCategory' } })
      // loại tài liệu
      getDropdownCatalog({ onSetDropdown: setdocumentType, params: { catalogTypeCode: 'DocumentType' } })

      getListCommonDate();

      debounceDocumentName()

      getDataDepartment();

      searchCreateBy();

      getDataCompany();
    }, [])

    return (
      // <PageWrapper
      //   breadcrumb={[
      //     {
      //       text: "Kho tài liệu",
      //     },
      //   ]}
      //   extras={
      //     <>
      //       {/* {checkPermission("CREATE") && ( */}
      //       <Link to={ROUTES.UTILITIES.ARCHIVES.LINK + '?type=create'}>
      //         <CustomButton
      //           type="primary"
      //           icon={<Plus className="!fill-white" />}
      //         >
      //           Thêm mới
      //         </CustomButton>
      //       </Link>
      //       {/* )} */}
      //     </>
      //   }
      // >
      <>
        <div className='flex justify-end m-1'>
          {checkPermission('CREATE') && (

            <Link to={ROUTES.UTILITIES.ARCHIVES.LINK + '?type=create'}>
              <CustomButton
                type="primary"
                icon={<Plus className="!fill-white" />}
              >
                Thêm mới
              </CustomButton>
            </Link>
          )}
        </div >
        <BoxContainer>
          <Form
            layout="horizontal"
            form={form}
            onFinish={(values) => {
              setLoading({ button: true })
              const ngayTao = values["createTime"];
              let fromTime = undefined;
              let toTime = undefined;
              if (ngayTao) {
                fromTime = ngayTao[0].format("YYYY-MM-DD") || undefined;
                toTime = ngayTao[1].format("YYYY-MM-DD") || undefined;
              }
              setDataPaging({
                ...values,
                description: values?.description ? values?.description : undefined,
                documentName: isEmpty(values?.documentName) ? undefined : values?.documentName,
                documentType: isEmpty(values?.documentType) ? undefined : values?.documentType,
                infoType: isEmpty(values?.infoType) ? undefined : values?.infoType,
                productRef: isEmpty(values?.productRef) ? undefined : values?.productRef,
                fromTime: fromTime,
                toTime: toTime,
                // actived: trangThai === null ? undefined : trangThai,
                paging: { ...dataPaging.paging, pageIndex: 1 },
              });
              tableRef.current?.reload();
              setLoading({ button: false })
            }}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 16 }}
            initialValues={{ actived: true, companyCode: getPlantCode() }}
          >
            <div className="w-full flex flex-col gap-2 md:grid md:grid-cols-2">
              {/* tên tài liệu */}
              <SearchItem name={"documentName"} label="Tên tài liệu">
                <CustomSelect
                  options={documentName}
                  mode='multiple'
                  fieldNames={{ label: "value", value: "key" }}
                  filterOption={false}
                  onSearch={(value) => {
                    if (value) {
                      debounceDocumentName(value)
                    }
                    else debounceDocumentName()
                  }}
                  // onBlur={() => debounceDocumentName()}
                  onClear={() => debounceDocumentName()}
                  allowClear
                  showSearch
                />
              </SearchItem>
              {/* loại tài liệu */}
              <SearchItem name={"documentType"} label="Loại tài liệu">
                <CustomSelect
                  options={documentType}
                  mode='multiple'
                  fieldNames={{ label: "value", value: "key" }}
                  filterOption={(input, option) =>
                    (option?.value ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  showSearch
                />
              </SearchItem>
              {/* Loại thông tin */}
              <SearchItem name={"infoType"} label="Loại thông tin">
                <CustomSelect options={infoType}
                  mode='multiple'
                  fieldNames={{ label: "value", value: "key" }}
                  filterOption={(input, option) =>
                    (option?.value ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  showSearch
                />
              </SearchItem>
              {/* Sản phẩm liên quan */}
              <SearchItem name={"productRef"} label="Sản phẩm liên quan">
                <CustomSelect options={listProduct}
                  mode='multiple'
                  fieldNames={{ label: "value", value: "key" }}
                  filterOption={(input, option) =>
                    (option?.value ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  showSearch
                />
              </SearchItem>
              {/* Mô tả chi tiết */}
              <SearchItem name={"description"} label="Mô tả chi tiết">
                <Input />
              </SearchItem>
              {/* Ngày tạo */}
              <SearchItem name={"commonDate"} label={"Ngày tạo"}>
                <CustomSelect
                  options={listCommonDate}
                  fieldNames={{ label: "value", value: "key" }}
                  onChange={async (value, option) => {
                    if (option) {
                      setDisableRangePicker(
                        option.key === "Custom" ? false : true
                      );
                      await getDateByKey(option.key);
                    } else {
                      setDisableRangePicker(false);
                      form.setFieldValue("createTime", undefined);
                    }
                  }}
                  allowClear
                />
              </SearchItem>
              {/* Trạng thái */}
              <SearchItem
                name={"actived"}
                label="Trạng thái"
              >
                <CustomSelect
                  onChange={(value) => {
                    debounceDocumentName()
                    form.setFieldValue('documentName', undefined)
                  }}
                  options={dropdownstatus} />
              </SearchItem>
              {/* chọn ngày*/}
              <SearchItem label={' '} colon={false} name={"createTime"}>
                <RangePicker
                  disabled={disableRangePicker}
                  format="DD/MM/YYYY"
                  className="w-full"
                />
              </SearchItem>

              {/* Phòng ban */}
              <SearchItem
                name={"departmentId"}
                label="Phòng ban"
              >
                <CustomSelect
                  options={listDepartment}
                  fieldNames={{ label: "value", value: "key" }}
                  filterOption={(input, option) =>
                    (option?.value ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  showSearch
                />
              </SearchItem>
              {/* Công ty */}
              <SearchItem
                name={"companyCode"}
                label={"Công ty"}
              >
                <CustomSelect
                  allowClear
                  onChange={() => {
                    debounceDocumentName()
                    getDataDepartment()
                  }}
                  options={listCompany}
                  fieldNames={{ label: "value", value: "key" }}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.value ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </SearchItem>

              {/* Người tạo */}
              <SearchItem name={'createBy'} label={t('search-form.created-by')}>
                <CustomSelect
                  options={listCreateBy}
                  fieldNames={{ label: 'value', value: 'key' }}
                  filterOption={false}
                  onSearch={(value) => {
                    searchCreateBy(value);
                  }}
                  allowClear
                  showSearch
                />
              </SearchItem>

              <div className="w-full md:col-span-2">
                <CustomButton
                  loading={loading.button}
                  className="mx-auto"
                  htmlType="submit"
                  type="primary"
                >
                  Tìm kiếm
                </CustomButton>
              </div>
            </div>
          </Form>
        </BoxContainer>
        <BoxContainer>
          <CustomProTable<ArchivesModel>
            options={false}
            handleResize={() => { }}
            bordered
            scroll={{ x: 2000, y: 300 }}
            dataSource={dataTable}
            columns={columns}
            actionRef={tableRef}
            request={async (params = {}, sort, filter) => {
              const data = await getListData({
                ...dataPaging,
                paging: {
                  ...dataPaging.paging,
                  orderBy:
                    Object.values(sort)[0] === "ascend"
                      ? `${Object.keys(sort)[0].split(",").pop()}`
                      : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === "descend"
                      ? `${Object.keys(sort)[0].split(",").pop()}`
                      : undefined,
                },
              });

              if (Object.values(sort).length) {
                setDataPaging({
                  ...dataPaging,
                  paging: {
                    ...dataPaging.paging,
                    orderBy:
                      Object.values(sort)[0] === "ascend"
                        ? `${Object.keys(sort)[0].split(",").pop()}`
                        : undefined,
                    orderByDesc:
                      Object.values(sort)[0] === "descend"
                        ? `${Object.keys(sort)[0].split(",").pop()}`
                        : undefined,
                  },
                });
              }
              return {
                data: data,
                success: documentArchiveStore.documentArchive.isSuccess,
              };
            }}
            rowKey="stt"
            pagination={{
              ...tablePagination(dataPaging, setDataPaging),
              total: documentArchiveStore.documentArchive.paging?.totalCount,
            }}
          />
        </BoxContainer>
        {/* </PageWrapper> */}
      </>
    );
  }
  )
}

export default createPageArchives